export const TrackingKey = {
  SCRIBE_LOGIN: 'perf.scribe.login',
  SCRIBE_SESSION_LIST_LOAD: 'perf.scribe.session_list_load',
  SCRIBE_NEW_SESSION: 'perf.scribe.new_session',
  SCRIBE_SESSION_OPEN: 'perf.scribe.session_open',
  SCRIBE_START_RECORDING: 'perf.scribe.start_recording',
  SCRIBE_PAUSE_RECORDING: 'perf.scribe.pause_recording',
  SCRIBE_RESUME_RECORDING: 'perf.scribe.resume_recording',
  SCRIBE_STOP_RECORDING: 'perf.scribe.stop_recording',
  SCRIBE_TAB_SWITCH: 'perf.scribe.tab_switch',
  SCRIBE_TEMPLATE_PICKER: 'perf.scribe.template_picker',
  SCRIBE_NOTE_GENERATION: 'perf.scribe.note_generation',
  SCRIBE_SECTIONAL_CONVERSION: 'perf.scribe.sectional_conversion',
  SCRIBE_NOTE_EDITOR_RENDER: 'perf.scribe.note_editor_render',
  SCRIBE_DOCUMENT_UPDATE: 'perf.scribe.document_update',
  SCRIBE_DOCUMENT_GENERATION: 'perf.scribe.document_generation',
  SCRIBE_CONTEXT_UPLOAD: 'perf.scribe.context_upload',
  SCRIBE_NOTE_UPDATE: 'perf.scribe.note_update',
  SCRIBE_RECORDING_FINALIZE: 'perf.scribe.recording_finalize',
  EVIDENCE_NEW_CHAT: 'perf.evidence.new_chat',
  EVIDENCE_CHAT_OPEN: 'perf.evidence.chat_open',
  EVIDENCE_MESSAGE_SEND: 'perf.evidence.message_send',
  EVIDENCE_SOURCE_CONTROL_OPEN: 'perf.evidence.source_control_open',
  EVIDENCE_IN_SESSION_SUGGESTION_CLICK: 'perf.evidence.in_session_suggestion_click',
  EVIDENCE_CONTEXT_ATTACH: 'perf.evidence.context_attach',
  TEMPLATE_INITIAL_STRUCTURE_GEN: 'perf.template.initial_structure_gen',
  TEMPLATE_EXAMPLE_GEN: 'perf.template.example_gen',
  TEMPLATE_STRUCTURE_CHAT_EDIT: 'perf.template.structure_chat_edit',
  TEMPLATE_EXAMPLE_CHAT_EDIT: 'perf.template.example_chat_edit',
  TEMPLATE_STRUCTURE_FROM_NOTE: 'perf.template.structure_from_note',
  TEMPLATE_EDITOR_RENDER: 'perf.template.editor_render',
  HAIKU_TERMINATE_TO_NOTE: 'perf.haiku.terminate_to_note',
  // App-shell flows: the left sidebar leads to every surface, so it is `app`,
  // not `scribe`. One key for the whole menu; the destination rides in metadata
  // (`nav_item`), the same way `SCRIBE_TAB_SWITCH` carries `tab_name`.
  APP_NAV_MENU_SWITCH: 'perf.app.nav_menu_switch',
  // Frame sampler (ARD-1977): one key for every watched surface; `surface`
  // and `trigger` ride in metadata alongside the frame counts. Opened
  // `instanced` with `userTiming: false`, and excluded from the abandonment
  // snapshot (src/features/performanceTracking/abandonment.ts): it is by far
  // the highest-frequency instanced flow, and an episode cut off by
  // `pagehide` carries no frame statistics worth replaying.
  APP_FRAME_EPISODE: 'perf.app.frame_episode',
} as const;

export type TrackingKey = (typeof TrackingKey)[keyof typeof TrackingKey];

export type TrackingType = 'interaction' | 'load';

export const TRACKING_TYPE_BY_KEY: Record<TrackingKey, TrackingType> = {
  [TrackingKey.SCRIBE_LOGIN]: 'load',
  [TrackingKey.SCRIBE_SESSION_LIST_LOAD]: 'load',
  [TrackingKey.SCRIBE_NEW_SESSION]: 'load',
  [TrackingKey.SCRIBE_SESSION_OPEN]: 'load',
  [TrackingKey.SCRIBE_START_RECORDING]: 'interaction',
  [TrackingKey.SCRIBE_PAUSE_RECORDING]: 'interaction',
  [TrackingKey.SCRIBE_RESUME_RECORDING]: 'interaction',
  [TrackingKey.SCRIBE_STOP_RECORDING]: 'interaction',
  [TrackingKey.SCRIBE_TAB_SWITCH]: 'interaction',
  [TrackingKey.SCRIBE_TEMPLATE_PICKER]: 'interaction',
  [TrackingKey.SCRIBE_NOTE_GENERATION]: 'load',
  [TrackingKey.SCRIBE_SECTIONAL_CONVERSION]: 'load',
  [TrackingKey.SCRIBE_NOTE_EDITOR_RENDER]: 'load',
  [TrackingKey.SCRIBE_DOCUMENT_UPDATE]: 'load',
  [TrackingKey.SCRIBE_DOCUMENT_GENERATION]: 'load',
  [TrackingKey.SCRIBE_CONTEXT_UPLOAD]: 'load',
  [TrackingKey.SCRIBE_NOTE_UPDATE]: 'load',
  [TrackingKey.SCRIBE_RECORDING_FINALIZE]: 'load',
  [TrackingKey.EVIDENCE_NEW_CHAT]: 'load',
  [TrackingKey.EVIDENCE_CHAT_OPEN]: 'load',
  [TrackingKey.EVIDENCE_MESSAGE_SEND]: 'interaction',
  [TrackingKey.EVIDENCE_SOURCE_CONTROL_OPEN]: 'interaction',
  [TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK]: 'interaction',
  [TrackingKey.EVIDENCE_CONTEXT_ATTACH]: 'interaction',
  [TrackingKey.TEMPLATE_INITIAL_STRUCTURE_GEN]: 'load',
  [TrackingKey.TEMPLATE_EXAMPLE_GEN]: 'load',
  [TrackingKey.TEMPLATE_STRUCTURE_CHAT_EDIT]: 'load',
  [TrackingKey.TEMPLATE_EXAMPLE_CHAT_EDIT]: 'load',
  [TrackingKey.TEMPLATE_STRUCTURE_FROM_NOTE]: 'load',
  [TrackingKey.TEMPLATE_EDITOR_RENDER]: 'load',
  [TrackingKey.HAIKU_TERMINATE_TO_NOTE]: 'load',
  [TrackingKey.APP_NAV_MENU_SWITCH]: 'interaction',
  [TrackingKey.APP_FRAME_EPISODE]: 'interaction',
};

export interface TrackingCheckpoint {
  name: string;
  timestamp: number;
}

export interface TrackingEntry {
  key: TrackingKey;
  instanceId: number;
  startTime: number;
  checkpoints: TrackingCheckpoint[];
  endTime?: number;
  metadata?: Record<string, unknown>;
  /** `false` when the run writes no user-timing marks or measures. */
  userTiming?: false;
}

export interface TrackingResult {
  key: TrackingKey;
  type: TrackingType;
  totalDurationMs: number;
  checkpointDurationsMs: Record<string, number>;
  phaseDurationsMs: Record<string, number>;
  metadata?: Record<string, unknown>;
}

/**
 * Opaque token returned by `startTracking({ instanced: true })`. Bundles the
 * key with a unique instance id so concurrent runs of the same key (e.g.
 * side-by-side note generation) target the right entry on checkpoint/finish/
 * cancel. Branded so it can only originate from `startTracking` — callers
 * cannot hand-roll one.
 */
export type TrackingHandle = {
  readonly key: TrackingKey;
  readonly instanceId: number;
} & { readonly __brand: 'TrackingHandle' };
